/**
 * API client per il backend `apps/core`.
 *
 * Aggiunge automaticamente:
 * - `X-Request-Id` (UUID generato lato client per correlation)
 * - `X-Stub-Email` (in dev locale, da VITE_STUB_EMAIL — usato solo dal backend in modalità stub)
 * - `Authorization: Bearer <id_token>` (Firebase ID token quando l'utente è autenticato)
 */

import { getCurrentIdToken } from "@/hooks/useFirebaseAuth";
import { getVediComeHeader } from "@/lib/vedi-come";

// In dev (`vite dev`): VITE_API_URL può essere vuoto → il proxy Vite
// (vite.config.ts) intercetta /api e /health e li gira al backend localhost.
// In build di produzione/staging: passare VITE_API_URL via --build-arg al docker
// per puntare al backend remoto (es. https://api-staging.misterpet.maitime.ai).
export const API_BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  // Stub auth in dev (header X-Stub-Email letto solo dal backend AUTH_BACKEND=stub)
  const stubEmail = import.meta.env.VITE_STUB_EMAIL;
  if (stubEmail) {
    headers["X-Stub-Email"] = stubEmail;
  }

  // Firebase ID token (presente quando l'utente è loggato).
  // Il backend in AUTH_BACKEND=firebase lo valida con Firebase Admin SDK;
  // in modalità stub viene ignorato.
  const token = await getCurrentIdToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // «Vedi come»: impersonazione per verifica (onorata dal backend solo se il
  // chiamante reale è MAITIME_STAFF, in sola lettura).
  const vediCome = getVediComeHeader();
  if (vediCome) {
    headers["X-Vedi-Come"] = vediCome;
  }

  return headers;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const requestId = crypto.randomUUID();

  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Request-Id": requestId,
      ...(await authHeaders()),
      ...(init.headers ?? {}),
    },
  });

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const message =
      typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, body);
  }

  return body as T;
}

/**
 * Scarica un file binario autenticato (bearer token via authHeaders).
 * Usa Blob + anchor download per trigger browser save-as.
 */
export async function apiDownloadFile(
  path: string,
  suggestedFilename?: string
): Promise<void> {
  const url = `${API_BASE}${path}`;
  const requestId = crypto.randomUUID();

  const response = await fetch(url, {
    headers: {
      "X-Request-Id": requestId,
      ...(await authHeaders()),
    },
  });

  if (!response.ok) {
    const text = await response.text();
    let body: unknown = text;
    try {
      body = JSON.parse(text);
    } catch {
      // body resta testo
    }
    const message =
      typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, body);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  if (suggestedFilename) {
    anchor.download = suggestedFilename;
  }
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Upload multipart/form-data. Differente da apiFetch perché non imposta
 * Content-Type (il browser ne genera uno con boundary). Stessi header auth.
 */
export async function apiUploadFile<T = unknown>(
  path: string,
  formData: FormData
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const requestId = crypto.randomUUID();

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-Request-Id": requestId,
      ...(await authHeaders()),
    },
    body: formData,
  });

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const message =
      typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, body);
  }

  return body as T;
}
